import { type FC, useCallback } from "react";

import { Checkbox } from "@snapchat/snap-design-system";
import { $enum } from "ts-enum-util";

import { SpecApprovalState } from "../../../../gql-generated/types";
import { StatusName, lensFilterItemContainer, widthName } from "../../../../lenses/components/list/header";
import { Flexbox } from "../../../../shared/components";

import { ApiFilterItem } from "./ApiFilterItem";
import { Container } from "./ApiFilterOptions.styles";
import { type StatusFilter } from "./models";

const listOfAllFilters = $enum(SpecApprovalState).getValues();

interface ApiFilterOptionsProps {
    onStatusFilter: StatusFilter;
    statusFilters: Set<SpecApprovalState>;
}

export const ApiFilterOptions: FC<ApiFilterOptionsProps> = ({ onStatusFilter, statusFilters }) => {
    const selectedAll = statusFilters.size === listOfAllFilters.length;

    const toggleAllSelected = useCallback(() => {
        if (selectedAll) {
            onStatusFilter(new Set());
        } else {
            onStatusFilter(new Set(listOfAllFilters));
        }
    }, [onStatusFilter, selectedAll]);

    const updateFilter = useCallback(
        (newFilter: SpecApprovalState) => {
            const updatedFilters = new Set(statusFilters);

            if (statusFilters.has(newFilter)) {
                updatedFilters.delete(newFilter);
            } else {
                updatedFilters.add(newFilter);
            }

            onStatusFilter(updatedFilters);
        },
        [onStatusFilter, statusFilters],
    );

    return (
        <Container>
            <Flexbox
                alignItems="center"
                justifyContent="space-between"
                className={lensFilterItemContainer}
                onClick={toggleAllSelected}
            >
                <Checkbox value={selectedAll} />
                <Flexbox alignItems="center" className={widthName}>
                    <StatusName>All</StatusName>
                </Flexbox>
            </Flexbox>
            {$enum(SpecApprovalState)
                .getValues()
                .map((value) => (
                    <ApiFilterItem
                        key={value}
                        value={value}
                        checked={statusFilters.has(value)}
                        updateFilter={updateFilter}
                    />
                ))}
        </Container>
    );
};
